/**
 * Spec §14.1 overview cards. Every value comes from a live endpoint; anything the backend
 * does not report to parents is rendered as "not reported" — never invented.
 */
import {
  useApps, useCapabilities, useConsents, useDevice, useHealth, usePolicies, useReports,
  useRequests, useUsage,
} from "../api/hooks.js";
import { CoverageNote } from "../components/StateBadge.js";
import { Empty, PageState } from "../components/PageState.js";

export function DashboardPage({ deviceId }: { deviceId: string }) {
  const device = useDevice(deviceId);
  const caps = useCapabilities(deviceId);
  const policies = usePolicies(deviceId);
  const usage = useUsage(deviceId);
  const apps = useApps(deviceId);
  const requests = useRequests(deviceId);
  const consents = useConsents(deviceId);
  const reports = useReports(deviceId);
  const health = useHealth();

  if (!deviceId) return <Empty label="No device selected — enroll a Quest headset first." />;

  const dailyCap = (policies.data ?? []).find((p) => p.type === "DEVICE_PLAYTIME_DAILY" && p.enabled);
  const usedSeconds = usage.data?.observed_seconds ?? 0;
  const remaining = dailyCap ? Math.max(0, Number(dailyCap.parameters.minutes ?? 0) - Math.round(usedSeconds / 60)) : null;
  const newest = (reports.data ?? [])[0];
  const activeApp = [...(apps.data ?? [])].sort((a, b) => a.observed_at.localeCompare(b.observed_at)).at(-1);
  const audioState = (consents.data ?? []).some((c) => !c.revoked_at) ? "CONSENT ACTIVE" : "NO CONSENT ON FILE";

  return (
    <PageState query={{ isPending: device.isPending, error: device.error }}>
      <div className="grid">
        <Card label="Device connection" value={device.data?.operational_state ?? "—"} hint={`last seen ${formatTime(device.data?.last_seen_at)}`} />
        <Card label="Firmware / OS" value={device.data?.os_version ?? "not reported"} hint="read from device registration" />
        <Card label="Battery" value={device.data?.battery_percent === null || device.data?.battery_percent === undefined ? "not reported" : `${device.data.battery_percent}%`} hint={`health reported ${formatTime(device.data?.health_reported_at)} (agent telemetry)`} />
        <Card label="Free storage" value={device.data?.storage_free_bytes ? formatBytes(device.data.storage_free_bytes) : "not reported"} hint="agent-reported data partition" />
        <Card label="Unsent events" value={device.data?.unsent_event_count === null || device.data?.unsent_event_count === undefined ? "not reported" : `${device.data.unsent_event_count} queued`} hint="offline queue depth on the headset" />
        <Card label="Active app" value={activeApp?.display_name ?? activeApp?.package_id ?? "unknown"} hint="from installed-app inventory" />
        <Card label="Last heartbeat" value={formatTime(device.data?.last_seen_at)} hint="server-side clock" />
        <Card label="ADB bridge" value={stateOf(caps, "adb_bridge")} hint="capability probe state" />
        <Card label="Network control" value={stateOf(caps, "network_control")} hint="router integration capability" />
        <Card label="Remaining allowance" value={remaining === null ? "no daily cap set" : `${remaining} min`} hint="daily cap minus observed playtime" />
        <Card label="New app discovered" value={`${(apps.data ?? []).length} tracked`} hint="third-party inventory size" />
        <Card label="Pending parent request" value={`${(requests.data ?? []).length} pending`} hint="child-initiated bonus asks" />
        <Card label="Audio recording state" value={audioState} hint="consent ledger status" />
        <Card label="AI pipeline health" value={health.data?.status ?? "unknown"} hint="worker/DB readiness" />
      </div>
      {newest ? (
        <section className="card stack">
          <h3>Tonight&apos;s report</h3>
          <CoverageNote coverage={newest.coverage} />
          <p className="muted">{newest.report_date_local} — status {newest.status}</p>
        </section>
      ) : (
        <p className="notice">No nightly report yet.</p>
      )}
      <p className="notice warn">
        Coverage: playtime is only as accurate as device reporting; unknown intervals are counted separately
        ({usage.data?.unknown_intervals ?? 0} this period).
      </p>
    </PageState>
  );
}

function Card({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="card">
      <h3>{label}</h3>
      <div className="value">{value}</div>
      <div className="hint">{hint}</div>
    </div>
  );
}

function stateOf(caps: ReturnType<typeof useCapabilities>, name: string) {
  const row = (caps.data ?? []).find((c) => c.capability === name);
  return row ? row.state : "untested";
}

function formatTime(iso: string | null | undefined) {
  return iso ? new Date(iso).toLocaleString() : "never";
}

/** Bytes only render as GiB/MiB when the agent actually reported them. */
function formatBytes(bytes: number) {
  const gib = bytes / 1024 ** 3;
  return gib >= 1 ? `${gib.toFixed(1)} GiB` : `${(bytes / 1024 ** 2).toFixed(1)} MiB`;
}
